import React, { useEffect, useRef, useState } from "react";
import CustomerNav from "@/components/layout/CustomerNav";
import { customerApi, formatApiError, API, CUSTOMER_TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Send, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Concierge — Claude Haiku 4.5-powered chat page on the customer portal.
// Streams responses from POST /api/concierge/message as Server-Sent Events;
// history rendering is DB-backed so a page reload keeps the transcript.
export default function Concierge() {
  const { customer } = useAuth();
  const [messages, setMessages] = useState([]);   // {role, content}
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState("");  // in-flight assistant text
  // Track remaining daily quota (server-authoritative). null while loading.
  const [remaining, setRemaining] = useState(null);
  const [cap, setCap] = useState(10);
  const bottomRef = useRef(null);

  // Pull the prior transcript once on mount. Server returns oldest-first.
  useEffect(() => {
    customerApi.get("/concierge/history")
      .then((r) => {
        setMessages(r.data?.messages || []);
        if (typeof r.data?.remaining_today === "number") setRemaining(r.data.remaining_today);
        if (typeof r.data?.daily_cap === "number") setCap(r.data.daily_cap);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  // Autoscroll to the latest turn whenever content changes.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, streaming]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setDraft("");
    // Optimistically show the user turn while we wait for the stream.
    setMessages((m) => [...m, { role: "user", content: text }]);
    setStreaming("");
    // Optimistically decrement the quota so the counter updates instantly;
    // if the request fails the catch/finally will refresh from history.
    setRemaining((n) => (typeof n === "number" ? Math.max(0, n - 1) : n));

    try {
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      const resp = await fetch(`${API}/concierge/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        body: JSON.stringify({ text }),
      });
      // Daily cap exceeded → server returns 429 with the friendly detail.
      // Roll back the optimistic user bubble and pin remaining=0 so the
      // input locks for the rest of today.
      if (resp.status === 429) {
        setMessages((m) => m.slice(0, -1));
        setDraft(text);
        setRemaining(0);
        let detail = `You've hit today's limit of ${cap} messages. Please come back tomorrow.`;
        try {
          const body = await resp.json();
          if (body?.detail) detail = body.detail;
        } catch { /* keep default */ }
        toast.error(detail);
        return;
      }
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames — split on blank line, each frame is `data: {json}`.
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const frame of parts) {
          const line = frame.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const ev = JSON.parse(payload);
            if (ev.delta) {
              accumulated += ev.delta;
              setStreaming(accumulated);
            } else if (ev.error) {
              throw new Error(ev.error);
            } else if (ev.done) {
              // Server has persisted the assistant turn. Commit locally.
              if (accumulated) {
                setMessages((m) => [...m, { role: "assistant", content: accumulated }]);
              }
              setStreaming("");
            }
          } catch (err) {
            if (err.message && !err.message.startsWith("HTTP")) throw err;
          }
        }
      }
    } catch (err) {
      toast.error(err?.message || "Concierge unavailable, please try again.");
      // Roll back the streaming buffer if we crashed mid-flight.
      setStreaming("");
    } finally {
      setBusy(false);
    }
  };

  const resetChat = async () => {
    if (!window.confirm("Clear this conversation? This can't be undone.")) return;
    try {
      await customerApi.post("/concierge/reset");
      setMessages([]);
      setStreaming("");
      toast.success("Conversation cleared.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const firstName = (customer?.first_name || customer?.full_name || "").split(" ")[0];

  return (
    <div className="min-h-screen bg-background">
      <CustomerNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-2">
              <Sparkles className="w-3.5 h-3.5" /> Cocoon Concierge
            </div>
            <h1 className="font-editorial text-3xl sm:text-4xl leading-tight">Ask us anything{firstName ? `, ${firstName}` : ""}.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg">
              Chat about services, prep tips, opening hours or what to expect on your visit. For bookings, medical questions or cancellations, we&apos;ll point you to the right place.
            </p>
          </div>
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={resetChat} data-testid="concierge-reset" className="rounded-full flex-shrink-0">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> New chat
            </Button>
          )}
        </div>

        <Card className="border-border">
          <CardContent className="p-0">
            <div className="h-[52vh] min-h-[380px] overflow-y-auto px-4 sm:px-6 py-4 space-y-4" data-testid="concierge-transcript">
              {messages.length === 0 && !streaming && (
                <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground text-sm gap-2">
                  <Sparkles className="w-6 h-6 opacity-40" />
                  <p>Say hi to get started — I&apos;m here to help.</p>
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} content={m.content} />
              ))}
              {streaming && <Bubble role="assistant" content={streaming} pending />}
              <div ref={bottomRef} />
            </div>

            {/* Locked-out banner replaces the composer when the guest has
                burned through today's cap. Kept inside the Card so the
                transcript is still visible above. */}
            {remaining === 0 ? (
              <div
                className="border-t border-border p-4 bg-amber-50 text-amber-900 text-sm flex items-start gap-3"
                data-testid="concierge-limit-banner"
              >
                <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">You&apos;ve hit today&apos;s limit of {cap} messages.</p>
                  <p className="text-xs mt-0.5 text-amber-800/80">The concierge will reset at midnight UTC. In the meantime, drop us a WhatsApp or head to the Book page to make a reservation.</p>
                </div>
              </div>
            ) : (
              <div className="border-t border-border p-3 sm:p-4 flex items-end gap-2">
                <Textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Type your question…"
                  rows={2}
                  className="resize-none flex-1"
                  disabled={busy}
                  data-testid="concierge-input"
                />
                <Button
                  onClick={send}
                  disabled={busy || !draft.trim()}
                  data-testid="concierge-send"
                  className="rounded-full h-10 w-10 p-0 flex-shrink-0"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mt-3 text-[11px] text-muted-foreground">
          <span>Cocoon Nest concierge — replies are AI-generated. Always double-check anything critical with our team.</span>
          {typeof remaining === "number" && (
            <span data-testid="concierge-quota">{remaining} of {cap} left today</span>
          )}
        </div>
      </main>
    </div>
  );
}

function Bubble({ role, content, pending }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-primary text-primary-foreground rounded-br-sm"
            : "bg-muted text-foreground rounded-bl-sm"
        }`}
        data-testid={`concierge-bubble-${role}`}
      >
        {content}
        {pending && <span className="inline-block w-1.5 h-3.5 ml-1 bg-current opacity-60 animate-pulse align-text-bottom" />}
      </div>
    </div>
  );
}
